import cn from 'classnames';
import { ClientEvents } from 'common/events/ClientEvents';
import { useEffect } from 'react';
import redAccent from 'src/assets/img/meal-plans/redAccent.png';
import shefCuttingBoard from 'src/assets/img/meal-plans/shefCuttingBoard.png';
import newTag from 'src/assets/svg/meal-plan/newTag.svg';
import weeklyShefService from 'src/assets/svg/meal-plan/weeklyShefService.svg';
import weeklyShefServiceSmall from 'src/assets/svg/meal-plan/weeklyShefServiceSmall.svg';
import { useMealPlanSurfacingLogic } from 'src/pages/consumer/meal-plans/hooks/useMealPlanSurfacingLogic';
import { useMealPlanDiscount } from 'src/pages/consumer/meal-plans/useMealPlanDiscount';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Icon, IconColor } from 'web-common/src/shared/design-system/components/Icon';
import { DishFirstExploreWidthLimit } from '../../../SharedStyles';
import { MEAL_PLAN_VALUE_PROPS } from '../../consts';
import { ReactComponent as CurlyArrowRight } from '../assets/curlyArrowRight.svg';

export const NewSubscriberBanner = () => {
  const history = useHistory();
  const { shouldShowMealPlanBanner, mealPlanIntakeRouteByRegion } = useMealPlanSurfacingLogic();
  const [currentUser] = useCurrentUser();
  const tracker = useTracker();
  const { promoEndText, totaledDiscountText } = useMealPlanDiscount();

  useEffect(() => {
    if (shouldShowMealPlanBanner) {
      tracker.track(ClientEvents.EXPLORE_MEAL_PLAN_HERO_BANNER_SHOWN, {
        userId: currentUser?.id,
        currentUserLastSearchedZipCode: currentUser?.lastSearchedZipCode,
      });
    }
  }, [shouldShowMealPlanBanner, currentUser?.id, currentUser?.lastSearchedZipCode, tracker]);

  const onClick = () => {
    tracker.track(ClientEvents.EXPLORE_MEAL_PLAN_HERO_BANNER_CLICK, {});
    history.push(mealPlanIntakeRouteByRegion);
  };

  return !shouldShowMealPlanBanner ? null : (
    <DishFirstExploreWidthLimit className='mt-4 px-4'>
      <div
        role='button'
        onClick={onClick}
        className='relative flex h-72 w-full cursor-pointer flex-col justify-end gap-4 overflow-hidden rounded-lg bg-sourdough px-4 py-8 legacy-sm:flex-row'>
        <div
          className={cn(
            '-z-1 absolute -left-4 top-0 h-full max-legacy-sm:-right-14 max-legacy-sm:-scale-x-100',
            'bg-contain bg-no-repeat legacy-sm:bg-cover'
          )}
          style={{
            backgroundImage: `url(${redAccent})`,
            backgroundPosition: 'left center',
          }}>
          <img src={shefCuttingBoard} className='h-full w-60 -scale-x-100 object-cover' alt='dishes' />
        </div>

        <div className='relative z-10 flex h-full grow flex-col items-end justify-center gap-4 max-legacy-sm:hidden legacy-lg:pr-6'>
          <img
            src={weeklyShefService}
            alt='weekly shef service'
            className='ml-4 max-h-[90%] grow-0 object-contain legacy-sm:w-3/5 legacy-lg:-mt-4'
          />
          <p className='mb-0 ml-4 w-3/5 text-center text-h5 text-eggplant max-legacy-lg:hidden'>
            Meals delivered every week from your shared personal chef
          </p>
          <div className='flex w-3/5 justify-center'>
            <CurlyArrowRight
              width={60}
              className='w-20 max-legacy-lg:ml-4 legacy-lg:absolute legacy-lg:right-0 legacy-lg:top-16 legacy-lg:rotate-[-75deg] legacy-lg:-scale-x-100'
            />
          </div>
        </div>

        <div className='grow-1 z-10 flex h-full flex-col justify-between max-legacy-sm:mr-16 legacy-sm:justify-center legacy-sm:gap-4 legacy-lg:pr-16'>
          <img
            src={newTag}
            alt='new!'
            className='absolute top-1 w-16 max-legacy-sm:left-1 legacy-sm:right-6 legacy-sm:top-6 legacy-sm:w-28 legacy-sm:rotate-[30deg]'
          />
          <img
            src={weeklyShefServiceSmall}
            alt='weekly shef service'
            className='my-2 mr-2 w-[95%] max-w-[350px] legacy-sm:hidden'
          />
          <div className='flex flex-col gap-1'>
            {MEAL_PLAN_VALUE_PROPS.map(({ copy, iconName }) => (
              <p
                className='m-0 flex items-center gap-1 text-label-700 text-eggplant legacy-sm:text-caption-700'
                key={copy}>
                <Icon name={iconName} color={IconColor.RASPBERRY} />
                {copy}
              </p>
            ))}
          </div>
          <div>
            <button className='mt-1 flex flex-nowrap items-center justify-center gap-2 rounded-full bg-mint-400 px-4 py-2 text-center uppercase text-white shadow-lg max-legacy-sm:px-6 legacy-sm:w-full'>
              <span className='m-0'>
                {totaledDiscountText ? (
                  <span className='flex flex-nowrap items-center gap-1 text-nowrap text-caption-700'>
                    Get <span className='mb-2 font-juniper-bay text-xl italic'>{totaledDiscountText}</span>{' '}
                    {promoEndText}
                  </span>
                ) : (
                  'Get started'
                )}
              </span>
              <Icon name='arrowRight' size={16} />
            </button>
          </div>
        </div>
      </div>
    </DishFirstExploreWidthLimit>
  );
};
